import * as React from "react";
import { CSSObject, styled, Theme, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import AppsIcon from "@mui/icons-material/Apps";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  position: "relative",
  height: "calc(100% - 15px)",
  overflowX: "hidden",
  backgroundColor: "#151515",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  position: "relative",
  height: "calc(100% - 15px)",
  backgroundColor: "#151515",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  position: "relative",
  whiteSpace: "nowrap",
  height: "calc(100% - 30px)",
  marginTop: "30px",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
    },
  }),
}));

export default function SystemSideBar() {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const navItems = [
    {
      to: "/system/organizations",
      icon: <CorporateFareIcon />,
      labelKey: "sidebar.system.organizations",
    },
    {
      to: "/system/apps",
      icon: <AppsIcon />,
      labelKey: "sidebar.system.apps",
    },
  ];

  return (
    <Box sx={{ display: "flex", top: "20px", height: "98%" }}>
      <Drawer variant="permanent" open={open}>
        <List style={{ padding: "0" }}>
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.to);
            return (
              <ListItem key={item.to} disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  component={Link}
                  to={item.to}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    backgroundColor: isActive
                      ? theme.palette.warning.main
                      : "inherit",
                    color: isActive ? "black" : "white",
                    "&:hover": {
                      backgroundColor: isActive
                        ? theme.palette.warning.main
                        : "#333",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "10px",
                      width: "30px",
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: isActive ? "black" : "white",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey)}
                    sx={{
                      opacity: open ? 1 : 0,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Divider />
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          color="inherit"
          onClick={() => setOpen(!open)}
          sx={{
            width: "100%",
            alignSelf: "center",
            marginBottom: 1,
            color: "#f0f0f0",
            borderRadius: "3px",
          }}
        >
          {open ? (
            <KeyboardDoubleArrowLeftIcon />
          ) : (
            <KeyboardDoubleArrowRightIcon />
          )}
        </IconButton>
      </Drawer>
    </Box>
  );
}
